import { useState, useRef } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { ArrowRight, CheckCircle, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SignupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "project_manager",
    password: "", note: "", company_logo_url: "",
  });
  const [logoPreview, setLogoPreview] = useState("");  // blob URL for instant preview
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (user && user !== null && user !== false) return <Navigate to="/dashboard" replace />;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Logo must be under 4 MB");
      return;
    }
    // Preview from the local File immediately — avoids the 401 that used
    // to break the preview <img> because /api/files/procure/signup-logos
    // is auth-gated and this page is by definition pre-authentication.
    const localUrl = URL.createObjectURL(file);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return localUrl;
    });
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/signup-logo-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, company_logo_url: data.logo_url }));
    } catch (err) {
      URL.revokeObjectURL(localUrl);
      setLogoPreview("");
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const clearLogo = () => {
    setLogoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    setForm((f) => ({ ...f, company_logo_url: "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/signup-request", form);
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const inputClass = "rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-11 px-4";

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center px-4 py-10 sm:py-16">
      {/* Floating nav */}
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-3xl bg-white/80 backdrop-blur-md rounded-full px-5 py-2.5 flex justify-between items-center shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-white/40">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <BrandLogo variant="compact" />
        </Link>
        <Link
          to="/login"
          className="text-xs sm:text-sm font-medium text-[#001E44] hover:text-[#007BFF] transition-colors"
          data-testid="nav-login-link"
        >
          Sign in →
        </Link>
      </nav>

      <div className="w-full max-w-md">
        {submitted ? (
          <div
            className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center"
            data-testid="signup-success"
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
              <CheckCircle size={28} weight="bold" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#001E44] mt-5">
              Request submitted
            </h2>
            <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
              Thanks {form.name.split(" ")[0]} — we&apos;ll review and approve your account.
              You&apos;ll be able to sign in with the password you just chose once it&apos;s approved.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 mt-6 text-sm text-[#007BFF] font-semibold hover:underline"
              data-testid="signup-back-to-login"
            >
              Back to sign in <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10">
            <div className="space-y-2 mb-7">
              <span className="inline-block label-eyebrow text-[#007BFF]">Request access</span>
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#001E44] leading-tight">
                Join liveprocure.com.
              </h1>
              <p className="text-sm text-zinc-500 leading-relaxed pt-1">
                Submit your details and we&apos;ll have you on site shortly.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Full name</Label>
                <Input required value={form.name} onChange={onChange("name")} data-testid="signup-name-input" className={inputClass} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Work email</Label>
                <Input required type="email" value={form.email} onChange={onChange("email")} data-testid="signup-email-input" className={inputClass} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Company</Label>
                <Input value={form.company} onChange={onChange("company")} placeholder="e.g. BuildCo" data-testid="signup-company-input" className={inputClass} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Company logo <span className="text-zinc-400 font-normal">(optional — appears on sample submission forms)</span></Label>
                {form.company_logo_url ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 bg-zinc-50">
                    <img
                      src={logoPreview || (form.company_logo_url.startsWith("http") ? form.company_logo_url : `${BACKEND}${form.company_logo_url}`)}
                      alt="Company logo"
                      className="h-10 w-10 rounded object-contain bg-white border border-zinc-100"
                      data-testid="signup-logo-preview"
                    />
                    <span className="text-xs text-zinc-500 flex-1 truncate">Logo attached</span>
                    <button type="button" onClick={clearLogo} className="p-1 rounded-full text-zinc-500 hover:bg-zinc-200" data-testid="signup-logo-clear" aria-label="Remove logo">
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="signup-logo-upload"
                    className="w-full h-11 rounded-xl bg-zinc-50 border border-dashed border-zinc-300 hover:border-[#007BFF] hover:bg-white text-xs font-semibold text-zinc-600 hover:text-[#007BFF] transition-colors flex items-center justify-center gap-2"
                  >
                    <UploadSimple size={14} weight="bold" />
                    {uploading ? "Uploading…" : "Upload logo (PNG, JPG, SVG, up to 4 MB)"}
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onChange={uploadLogo}
                  className="hidden"
                  data-testid="signup-logo-file-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">I&apos;ll be using liveprocure.com as</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="signup-role-select" className="rounded-xl bg-zinc-50 border-zinc-200 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_manager" data-testid="signup-role-pm">Project Manager</SelectItem>
                    <SelectItem value="contractor" data-testid="signup-role-contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Choose a password</Label>
                <Input required type="password" minLength={6} value={form.password} onChange={onChange("password")} data-testid="signup-password-input" className={inputClass} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Notes for the admin (optional)</Label>
                <Textarea
                  rows={2} value={form.note} onChange={onChange("note")}
                  placeholder="Tell us about your project / how you heard about us"
                  data-testid="signup-note-input"
                  className="rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 px-4 py-3"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#007BFF] hover:bg-[#0062cc] rounded-full h-12 text-sm font-semibold shadow-sm hover:shadow-md transition-all mt-2"
                data-testid="signup-submit"
              >
                {busy ? "Submitting..." : <>Request account <ArrowRight size={16} weight="bold" className="ml-2" /></>}
              </Button>

              <p className="text-sm text-zinc-500 text-center pt-1">
                Already approved?{" "}
                <Link to="/login" className="text-[#007BFF] font-semibold hover:underline" data-testid="link-login">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
